import styled from '@emotion/styled'
import { ExtranetCustomerListItemDto } from '@pharmac-extranet/extranet-api'
import { useDebounce, useIntersectionObserver, useLanguage } from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacLoadingDots } from '@pharmac/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { getPages } from '../../features/infinite-query/util/getPages'
import { api } from '../../services/HttpService'

const CustomerSelectorRoot = styled.div`
  position: relative;
  width: 300px;
`

const DropdownSearch = styled.div<{ isOpen: boolean }>`
  font-size: 1em;
  font-family: var(--font-family-primary);
  width: 300px;
  outline: none;
  border: ${({ isOpen }) =>
    isOpen ? '2px solid var(--color-primary)' : '2px solid var(--color-accent-4)'};

  line-height: var(--input-line-height);

  border-radius: ${({ isOpen }) => (isOpen ? '18px 18px 0 0' : '18px')};
  border-bottom: ${({ isOpen }) =>
    isOpen ? '2px solid transparent' : '2px solid var(--color-accent-4)'};

  overflow: hidden;
  height: 39px;

  z-index: 50;

  span {
    display: ${({ isOpen }) => (isOpen ? 'none' : 'block')};
    padding: var(--input-medium-padding-vertical) var(--input-medium-padding-horizontal);
    width: 80%;
  }

  input {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};

    border: 0;
    outline: none;
    padding: var(--input-medium-padding-vertical) var(--input-medium-padding-horizontal);
    font-size: var(--input-medium-font-size);
    color: var(--color-accent-7);

    line-height: var(--input-medium-line-height);
    width: 80%;
  }
`

const DropdownItems = styled.div`
  position: absolute;
  width: 300px;
  background-color: var(--color-white);
  border-radius: 0 0 18px 18px;
  border: 2px solid var(--color-primary);
  border-top: none;
  top: calc(100% - 1px);
  border-top: 1px solid var(--color-accent-4);
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;

  z-index: 50;
`

const DropdownItem = styled.div`
  button,
  span {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    min-height: 36px;
    width: 100%;
    padding: var(--spacing-tiny) var(--spacing-small);
    :hover {
      cursor: pointer;
      background-color: var(--color-accent-2);
    }
  }
`

const DropdownChevron = styled.button<{ isOpen?: boolean }>`
  position: absolute;
  right: var(--spacing-small);
  top: 0;

  display: flex;
  padding-top: 8px;

  svg {
    height: 24px;
    width: 24px;

    transition: var(--transition-cubic-bezier);

    color: var(--color-primary);

    transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`

const DropdownRemoveButton = styled.div`
  position: absolute;
  left: calc(100% + 10px);
  top: calc(50% - 12px);
  width: 24px;
  height: 24px;

  background-color: var(--color-primary);
  border-radius: 50%;

  transition: var(--transition-cubic-bezier);
  box-shadow: var(--shadow-tiny);

  :hover {
    cursor: pointer;

    transform: var(--button-primary-transform-hover);
    box-shadow: var(--shadow-tiny);
  }

  svg {
    transition: var(--transition-cubic-bezier);
    color: var(--color-white);
  }
`

export const CustomerSelector = () => {
  const [isOpen, setIsOpen] = useState(false)

  const { t } = useLanguage()

  const { setCustomer, customer } = useCustomerContext()

  const searchRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { user, isPharmacCustomerService } = useAuthenticationContext()

  const [debounced, setValue, value] = useDebounce('', 500)

  const fetchPage = async ({ pageParam = 0 }) => {
    const params: any = {
      page: pageParam,
      pageSize: 3,
      name: debounced,
    }

    const response = await api.customers.getPagedCustomers(params)

    return response
  }

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['__getCustomerServiceCustomers', { search: debounced }],
    queryFn: ({ pageParam }) => fetchPage({ pageParam }),
    initialPageParam: 1,
    enabled: !!debounced && debounced.length > 2,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const [nodeRef] = useIntersectionObserver((entries) => {
    if (entries.some((a) => a.isIntersecting)) {
      fetchNextPage()
    }
  })

  const customers = getPages<ExtranetCustomerListItemDto>(data)

  useEffect(() => {
    const openDropdown = () => {
      setIsOpen(true)
    }

    const outsideClickHandler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    if (inputRef.current) {
      inputRef.current.addEventListener('click', openDropdown)
    }

    if (searchRef.current) {
      if (isOpen) {
        window.addEventListener('click', outsideClickHandler)
      }
    }

    return () => {
      window.removeEventListener('click', outsideClickHandler)
      inputRef.current?.removeEventListener('click', openDropdown)
    }
  }, [inputRef, searchRef, isOpen])

  const hasSearchInput = !!debounced && debounced.length > 1

  const handleSelectCustomer = (customer: ExtranetCustomerListItemDto) => {
    setCustomer(customer, customer)
    setValue('')
    setIsOpen(false)
  }

  const handleRemoveCustomer = () => {
    setCustomer(false)
    setValue('')
    setIsOpen(false)
  }

  if (!user || !isPharmacCustomerService) {
    return null
  }

  return (
    <CustomerSelectorRoot ref={searchRef}>
      <DropdownSearch
        isOpen={isOpen}
        ref={inputRef}
      >
        <span>
          {customer?.name || t('extranet:customerService.customerSelectInput.placeholder')}
        </span>
        <input
          ref={inputRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <DropdownChevron isOpen={isOpen}>
          <PharmacIcon icon="chevronDown" />
        </DropdownChevron>
        {customer && (
          <DropdownRemoveButton onClick={handleRemoveCustomer}>
            <PharmacIcon
              icon="x"
              size="24"
            />
          </DropdownRemoveButton>
        )}
      </DropdownSearch>
      {isOpen && (
        <DropdownItems className="enscrollbar">
          {!hasSearchInput && (
            <DropdownItem>
              <span>{t('extranet:customerService.customerSelectInput.searchInstruction')}</span>
            </DropdownItem>
          )}
          {hasSearchInput && customers.length === 0 && (
            <DropdownItem>{t('common:noSearchResultsTitle')}</DropdownItem>
          )}
          {customers.length > 0 && hasSearchInput && (
            <>
              {customers.map((customer) => (
                <DropdownItem key={customer.id}>
                  <button onClick={() => handleSelectCustomer(customer)}>{customer.name}</button>
                </DropdownItem>
              ))}
            </>
          )}
          {hasNextPage && (
            <DropdownItem ref={nodeRef}>
              {isFetchingNextPage && <PharmacLoadingDots />}
            </DropdownItem>
          )}
        </DropdownItems>
      )}
    </CustomerSelectorRoot>
  )
}
